import { useEffect, useState } from 'react';
import { Banner, BlockStack, InlineStack, Link, List, Spinner, Text } from '@shopify/polaris';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_FAILURES = 3;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
};

function DocumentCopyStatus({ job }) {
  if (job.status !== 'done') {
    return (
      <InlineStack gap="100" blockAlign="center" wrap={false}>
        <Spinner size="small" />
        <Text as="span" variant="bodySm" tone="subdued">
          {`Copying part documents ${Math.min(job.processed, job.total)} of ${job.total}${job.currentFilename ? ` · ${job.currentFilename}` : ''}`}
        </Text>
      </InlineStack>
    );
  }
  const copied = job.copied?.length || 0;
  return (
    <BlockStack gap="050">
      <Text as="span" variant="bodySm" tone={job.warnings?.length ? 'caution' : 'success'}>
        {copied === job.total ? `${copied} part document(s) copied` : `${copied} of ${job.total} part document(s) copied`}
      </Text>
      {(job.warnings || []).map((warning) => (
        <Text key={warning} as="span" variant="bodySm" tone="subdued">
          {warning}
        </Text>
      ))}
    </BlockStack>
  );
}

/*
  Result of "Create purchase orders" in the RWS BOM tool: links to the new orders and the progress of the part
  documents copied onto them on the server (polls GET /api/reports/bom-cost-analysis/document-jobs/:jobId).
  Mount it with a key per result; leaving the page does not stop the copy.
*/
export default function BomPurchaseOrdersBanner({ result, onDismiss }) {
  const [jobs, setJobs] = useState(() => new Map(result.createdOrders.filter((order) => order.fileCopy).map((order) => [order.purchaseId, order.fileCopy])));
  const [pollError, setPollError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const latest = new Map(jobs);

    const poll = async () => {
      let failures = 0;
      while (!cancelled) {
        const running = [...latest.values()].filter((job) => job.status !== 'done');
        if (running.length === 0) return;
        await sleep(POLL_INTERVAL_MS);
        try {
          const snapshots = await Promise.all(
            running.map(async (job) => {
              const response = await fetch(`/api/reports/bom-cost-analysis/document-jobs/${job.jobId}`);
              const snapshot = await response.json();
              if (!response.ok) throw new Error(snapshot?.message || 'Could not read the document copy progress.');
              return snapshot;
            })
          );
          if (cancelled) return;
          failures = 0;
          snapshots.forEach((snapshot) => latest.set(snapshot.targetPurchaseId, snapshot));
          setJobs(new Map(latest));
        } catch (error) {
          failures += 1;
          if (failures >= MAX_POLL_FAILURES) {
            if (!cancelled) setPollError(`${error.message} The server keeps copying; the documents appear on the purchase orders once finished.`);
            return;
          }
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // the jobs of this result are polled once, from the snapshots the create call returned
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobList = [...jobs.values()];
  const copying = jobList.some((job) => job.status !== 'done');
  const hasWarnings = jobList.some((job) => job.status === 'done' && job.warnings?.length);

  return (
    <Banner tone={hasWarnings || pollError ? 'warning' : 'success'} title={`${result.totalSuppliers} purchase order${result.totalSuppliers === 1 ? '' : 's'} created`} onDismiss={onDismiss}>
      <BlockStack gap="200">
        <List>
          {result.createdOrders.map((order) => (
            <List.Item key={order.purchaseId}>
              <BlockStack gap="050">
                <span>
                  <Link url={`/purchases/${order.purchaseId}`}>PO {order.purchaseNumber}</Link> — {order.supplierName || `Supplier ${order.supplierId}`} ({order.lineCount} lines,{' '}
                  {formatCurrency(order.lineTotal)})
                </span>
                {jobs.has(order.purchaseId) && <DocumentCopyStatus job={jobs.get(order.purchaseId)} />}
              </BlockStack>
            </List.Item>
          ))}
        </List>
        {copying && !pollError && (
          <Text as="p" variant="bodySm" tone="subdued">
            The orders can be opened already; the part documents keep copying in the background.
          </Text>
        )}
        {pollError && (
          <Text as="p" variant="bodySm" tone="caution">
            {pollError}
          </Text>
        )}
      </BlockStack>
    </Banner>
  );
}
